import { useLocation, useNavigate } from 'react-router';
import { PaletteColor } from '@mui/material';
import { IconName } from '@fortawesome/pro-duotone-svg-icons';
import { MenuItemProps } from '../../molecules/MenuItem';
import { MenuEntry } from './MenuEntry';

interface BestMatch {
    path: string;
    ancestors: string[];
}

/**
 * Hook personnalisé pour gérer la logique de la barre latérale de navigation.
 * Fournit des utilitaires pour convertir les entrées de menu, déterminer les éléments actifs et ouverts.
 */
export const useSidebar = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();

    /**
     * Convertit une entrée de menu en élément de menu avec toutes ses propriétés
     */
    const entryToItem = (entry: MenuEntry, color: PaletteColor, folded: boolean): MenuItemProps => {
        const item: MenuItemProps = {
            identifier: entry.title,
            label: entry.title,
            icon: {
                icon: ['fasl', 'question'],
                sx: { display: 'flex', alignItems: 'center' },
            },
            onClick: (target: string | null) => {
                if (target) {
                    navigate(target);
                }
            },
            href: entry.href,
            color,
            childrenItems: [],
            folded,
        };

        if (entry?.children) {
            item.childrenItems = entry.children.map((subEntry) =>
                entryToItem(subEntry, color, folded),
            );
        }

        if (entry?.icon) {
            item.icon = {
                ...item.icon,
                icon: ['fasl', entry.icon as IconName],
            };
        }

        return item;
    };

    /**
     * Trouve les éléments actifs dans l'arbre de navigation en cherchant la correspondance la plus spécifique.
     * Utilise un algorithme de "longest match" pour déterminer quelle route correspond le mieux au chemin actuel.
     */
    const findActiveElements = (itemsTree: MenuItemProps[]): string[] => {
        let bestMatch: BestMatch | null = null;

        const searchBestMatch = (items: MenuItemProps[], ancestors: string[]): void => {
            items.forEach((item) => {
                const currentHref = item?.href;

                if (currentHref && pathname.startsWith(currentHref)) {
                    const currentMatch: BestMatch = {
                        path: currentHref,
                        ancestors: [...ancestors, item.identifier],
                    };

                    if (!bestMatch || currentHref.length > bestMatch.path.length) {
                        bestMatch = currentMatch;
                    }
                }

                const children = item?.childrenItems || [];
                if (children.length > 0) {
                    searchBestMatch(children, [...ancestors, item.identifier]);
                }
            });
        };

        searchBestMatch(itemsTree, []);

        return (bestMatch as unknown as BestMatch)?.ancestors || [];
    };

    /**
     * Trouve les éléments qui doivent être ouverts dans la navigation.
     * Exclut le dernier élément de l'arbre actif (qui correspond à la page courante).
     */
    const findOpenElements = (items: MenuItemProps[]): string[] => {
        const activeTree = findActiveElements(items);
        return activeTree.slice(0, -1);
    };

    return {
        entryToItem,
        findActiveElements,
        findOpenElements,
    };
};
